import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import ChatFeedbackService from './ChatFeedbackService';
import { CaseReader, PageChip, SevChip, StatusChip, RoleChip, fmtDue, fmtDateTime, fmtSeconds, isOverdue, modalStyles } from './chatFeedbackShared';

/**
 * Dashboard Feedback แชท (supervisor only)
 *
 * รอตรวจ   everything waiting on me: disputes, overdue cases, answers to judge
 * รายคน    per-admin numbers for the date range; click a row for their cases
 * หลังบ้าน system-side problems: who took it, note, done
 */

const TABS = [
  { key: 'queue', label: 'รอตรวจ' },
  { key: 'admins', label: 'รายคน' },
  { key: 'backoffice', label: 'หลังบ้าน' },
];
const isoDate = (d) => new Date(d).toISOString().slice(0, 10);
const errMsg = (err, fallback) => err.response?.data?.message || fallback;

/* ---------- full case in a modal ---------- */

function CaseModal({ recipientId, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!recipientId) return;
    ChatFeedbackService.getRecipient(recipientId).then((res) => setDetail(res.data)).catch((err) => toast.error(errMsg(err, 'โหลดเคสไม่สำเร็จ')));
  }, [recipientId]);
  return (
    <Modal isOpen={!!recipientId} onRequestClose={onClose} style={modalStyles} ariaHideApp={false}>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="font-semibold text-gray-800">เคสเต็ม</div>
          <button type="button" onClick={onClose} className="ml-auto text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        {detail ? <CaseReader caseData={detail.case} recipient={detail} viewerName={detail.admin?.nickname} /> : <div className="text-gray-400">กำลังโหลด...</div>}
      </div>
    </Modal>
  );
}

/* ---------- รอตรวจ ---------- */

function ReviewQueue({ filters, onOpenCase }) {
  const [items, setItems] = useState([]);
  const [reply, setReply] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    ChatFeedbackService.getReviewQueue(filters).then((res) => setItems(res.data)).catch((err) => toast.error(errMsg(err, 'โหลดไม่สำเร็จ')));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, fn, okText) => {
    setBusyId(id);
    try {
      await fn();
      toast.success(okText);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'ไม่สำเร็จ'));
    } finally {
      setBusyId(null);
    }
  };
  const verdict = (r, v, comment) => act(r.id, () => ChatFeedbackService.setVerdict(r.id, { verdict: v, comment }), 'บันทึกแล้ว');
  const uphold = (r) => {
    const reason = window.prompt('เหตุผลที่ยืนยันเคส (แอดมินต้องกลับไปรับทราบ):');
    if (reason && reason.trim()) verdict(r, 'upheld', reason.trim());
  };
  const send = (r) => act(r.id, () => ChatFeedbackService.createComment(r.id, reply[r.id]), 'ส่งแล้ว').then(() => setReply((p) => ({ ...p, [r.id]: '' })));
  const move = (r) => window.confirm('ย้ายเคสนี้ไปหลังบ้าน? จะถอนออกจากทุกคนที่ได้รับ') && act(r.id, () => ChatFeedbackService.updateBackoffice(r.caseId, { move: true }), 'ย้ายแล้ว');

  if (items.length === 0) return <div className="text-center text-gray-400 py-6">ไม่มีอะไรรอตรวจ</div>;
  return (
    <div className="space-y-3">
      {items.map((r) => (
        <div key={r.id} className={`bg-white rounded-xl border p-4 space-y-3 ${r.overdue ? 'border-red-400' : r.status === 'discussing' ? 'border-amber-400' : 'border-gray-300'}`}>
          <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
            <span className="text-sm font-semibold text-gray-800">{r.admin?.nickname}</span>
            <StatusChip status={r.status} /><RoleChip role={r.role} />
            {r.disputedAt && <span className="text-amber-700 font-semibold">ขอชี้แจง</span>}
            {r.overdue && <span className="text-red-600 font-semibold">เลยกำหนด {fmtDue(r.dueAt)}</span>}
            <button type="button" onClick={() => onOpenCase(r.id)} className="ml-auto text-indigo-600 hover:text-indigo-800">ดูเคสเต็ม</button>
          </div>
          <div className="text-sm">
            <div className="font-semibold text-gray-800">{r.case.title || r.case.category}</div>
            <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500 mt-0.5">
              <PageChip page={r.case.page} /><SevChip sev={r.case.severity} /><span>{r.case.category}</span><span>{r.case.reportDate}</span>
            </div>
            <div className="mt-1 text-gray-700 line-clamp-2">{r.personalWhat || r.case.issue}</div>
          </div>

          {r.answer && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm space-y-1">
              <div className="text-xs text-blue-800 font-semibold">แอดมินตอบ · {fmtDateTime(r.ackAt)} · เปิดอ่าน {fmtSeconds(r.readSeconds)}{r.readSeconds != null && r.readSeconds < 10 ? ' ⚠ อ่านเร็วมาก' : ''}</div>
              <div className="whitespace-pre-wrap">{r.answer}</div>
              {r.answerNext && <div className="pt-1 border-t border-blue-200"><span className="text-xs font-semibold">ครั้งหน้า: </span>{r.answerNext}</div>}
            </div>
          )}

          {r.comments.length > 0 && (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm space-y-2">
              {r.comments.map((c) => (
                <div key={c.id}><span className="text-xs text-gray-500">{c.author?.nickname} · {fmtDateTime(c.createdAt)}: </span>{c.content}</div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            {r.status === 'answered' && (
              <button type="button" disabled={busyId === r.id} onClick={() => verdict(r, 'understood')} className="bg-green-600 text-white rounded-md px-3 py-1.5 text-sm hover:bg-green-700 disabled:opacity-50">เข้าใจแล้ว ปิดเคส</button>
            )}
            {r.status === 'discussing' && (
              <>
                <button type="button" disabled={busyId === r.id} onClick={() => verdict(r, 'understood')} className="bg-green-600 text-white rounded-md px-3 py-1.5 text-sm hover:bg-green-700 disabled:opacity-50">จบ ปิดเคส</button>
                <button type="button" disabled={busyId === r.id} onClick={() => verdict(r, 'withdrawn', 'ยอมรับคำชี้แจง ถอนเคส')} className="border border-gray-400 rounded-md px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50">ถอนเคส (ไม่นับ)</button>
                <button type="button" disabled={busyId === r.id} onClick={() => uphold(r)} className="border border-red-400 text-red-700 rounded-md px-3 py-1.5 text-sm hover:bg-red-50 disabled:opacity-50">ยืนยันเคส</button>
              </>
            )}
            {r.status !== 'pending' && (
              <span className="flex gap-1 ml-auto">
                <input value={reply[r.id] || ''} onChange={(e) => setReply((p) => ({ ...p, [r.id]: e.target.value }))} placeholder="ถามต่อ / ตอบกลับ..." className="border rounded px-2 py-1 text-sm w-56" />
                <button type="button" disabled={!(reply[r.id] || '').trim() || busyId === r.id} onClick={() => send(r)} className="border border-gray-400 rounded-md px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50">ส่ง</button>
              </span>
            )}
            <button type="button" disabled={busyId === r.id} onClick={() => move(r)} className="text-xs text-gray-500 hover:text-gray-800 underline">ย้ายไปหลังบ้าน</button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- รายคน ---------- */

function AdminStats({ filters, onOpenCase }) {
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null); // { adminId, nickname, items }

  useEffect(() => {
    ChatFeedbackService.getDashboard(filters).then((res) => setData(res.data)).catch((err) => toast.error(errMsg(err, 'โหลดไม่สำเร็จ')));
  }, [filters]);

  const openAdmin = async (a) => {
    try {
      const res = await ChatFeedbackService.getInbox({ adminId: a.adminId });
      setDrill({ adminId: a.adminId, nickname: a.nickname, items: res.data.items });
    } catch (err) {
      toast.error(errMsg(err, 'โหลดไม่สำเร็จ'));
    }
  };

  if (!data) return <div className="text-center text-gray-400 py-6">กำลังโหลด...</div>;
  const th = 'px-2 py-2 text-left text-xs font-semibold text-gray-600 whitespace-nowrap';
  const td = 'px-2 py-2 text-sm whitespace-nowrap';
  return (
    <div className="space-y-4">
      {data.batch && (
        <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm flex gap-4 flex-wrap">
          <span className="font-semibold text-gray-800">รายงานล่าสุด {data.batch.reportDate}</span>
          <span>ส่ง {data.batch.sent}</span><span>รับทราบ {data.batch.acked}</span>
          <span className={data.batch.overdue ? 'text-red-600 font-semibold' : ''}>เลยกำหนด {data.batch.overdue}</span>
          <span>รอตรวจ {data.batch.waitingReview}</span>
        </div>
      )}
      <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr>
            {['แอดมิน', 'แชท', 'ต้องปรับ', 'ต่อ 100 แชท', 'วิกฤต+สูง', 'ซ้ำใน 30 วัน', 'เรียนรู้', 'ถอน', 'รับทราบเฉลี่ย (ชม.)', 'เปิดอ่านเฉลี่ย', 'ค้าง', 'เลยกำหนด', 'ชี้แจง'].map((h) => <th key={h} className={th}>{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {data.admins.map((a) => (
              <tr key={a.adminId} onClick={() => openAdmin(a)} className="hover:bg-indigo-50 cursor-pointer">
                <td className={`${td} font-semibold text-gray-800`}>{a.nickname}</td>
                <td className={td}>{a.chats || '-'}</td>
                <td className={`${td} font-semibold ${a.total ? 'text-red-700' : 'text-gray-400'}`}>{a.total}</td>
                <td className={td}>{a.per100 ?? '-'}</td>
                <td className={td}>{a.critHigh || '-'}</td>
                <td className={`${td} ${a.repeats ? 'text-red-700 font-semibold' : ''}`}>{a.repeats || '-'}</td>
                <td className={td}>{a.learnTotal || '-'}</td>
                <td className={td}>{a.withdrawn || '-'}</td>
                <td className={td}>{a.avgAckHours ?? '-'}</td>
                <td className={td}>{a.avgReadSeconds != null ? fmtSeconds(a.avgReadSeconds) : '-'}</td>
                <td className={td}>{a.pending || '-'}</td>
                <td className={`${td} ${a.overdue ? 'text-red-700 font-semibold' : ''}`}>{a.overdue || '-'}</td>
                <td className={td}>{a.disputed || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.weekly.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-3 text-sm">
          <div className="font-semibold text-gray-800 mb-2">แนวโน้มรายสัปดาห์ (เคสต้องปรับ)</div>
          {data.weekly.map((w) => (
            <div key={w.week} className="flex gap-3 flex-wrap py-1 border-t border-gray-100">
              <span className="w-24 text-gray-500">{w.week}</span><span className="font-semibold">{w.total}</span>
              <span className="text-gray-600">{Object.entries(w.byCategory).map(([c, n]) => `${c} ${n}`).join(' · ')}</span>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={!!drill} onRequestClose={() => setDrill(null)} style={modalStyles} ariaHideApp={false}>
        {drill && (
          <div className="p-5 space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <div className="font-semibold text-gray-800">เคสของ {drill.nickname}</div>
              <button type="button" onClick={() => setDrill(null)} className="ml-auto text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>
            {drill.items.length === 0 && <div className="text-gray-400 text-sm">ไม่มีเคส</div>}
            {drill.items.map((r) => (
              <button key={r.id} type="button" onClick={() => onOpenCase(r.id)} className={`w-full text-left rounded-lg border px-3 py-2 hover:bg-gray-50 ${r.overdue ? 'border-red-400' : 'border-gray-200'}`}>
                <div className="text-sm font-semibold text-gray-800">{r.case.title || r.case.category}</div>
                <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500 mt-0.5">
                  <StatusChip status={r.status} /><RoleChip role={r.role} /><SevChip sev={r.case.severity} /><span>{r.case.reportDate}</span>
                  {r.status === 'pending' && r.dueAt && <span className={isOverdue(r) ? 'text-red-600 font-semibold' : ''}>ก่อน {fmtDue(r.dueAt)}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ---------- หลังบ้าน ---------- */

function Backoffice() {
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({});

  const load = useCallback(() => {
    ChatFeedbackService.getBackoffice().then((res) => setItems(res.data)).catch((err) => toast.error(errMsg(err, 'โหลดไม่สำเร็จ')));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (c, patch) => {
    try {
      await ChatFeedbackService.updateBackoffice(c.id, patch);
      toast.success('บันทึกแล้ว');
      setEdits((p) => { const n = { ...p }; delete n[c.id]; return n; });
      load();
    } catch (err) {
      toast.error(errMsg(err, 'ไม่สำเร็จ'));
    }
  };

  if (items.length === 0) return <div className="text-center text-gray-400 py-6">ไม่มีปัญหาหลังบ้าน</div>;
  return (
    <div className="space-y-3">
      {items.map((c) => {
        const e = edits[c.id] || { owner: c.backofficeOwner || '', note: c.backofficeNote || '' };
        const done = c.backofficeStatus === 'done';
        return (
          <div key={c.id} className={`bg-white rounded-xl border p-4 space-y-2 ${done ? 'border-gray-200 opacity-70' : 'border-amber-300'}`}>
            <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
              <span className="text-sm font-semibold text-gray-800">{c.title || c.category}</span>
              <PageChip page={c.page} /><SevChip sev={c.severity} /><span>{c.reportDate}</span>
              <span className={`ml-auto font-semibold ${done ? 'text-green-700' : 'text-amber-700'}`}>{done ? `เสร็จแล้ว ${fmtDateTime(c.backofficeDoneAt)}` : 'ยังไม่แก้'}</span>
            </div>
            <div className="text-sm text-gray-700 whitespace-pre-wrap">{c.issue}</div>
            {c.fix && <div className="text-sm text-green-800 whitespace-pre-wrap">→ {c.fix}</div>}
            <div className="flex gap-2 flex-wrap items-center pt-1">
              <input value={e.owner} onChange={(ev) => setEdits((p) => ({ ...p, [c.id]: { ...e, owner: ev.target.value } }))} placeholder="ใครรับไปแก้" className="border rounded px-2 py-1 text-sm w-32" />
              <input value={e.note} onChange={(ev) => setEdits((p) => ({ ...p, [c.id]: { ...e, note: ev.target.value } }))} placeholder="โน้ต" className="border rounded px-2 py-1 text-sm flex-1 min-w-[12rem]" />
              <button type="button" onClick={() => save(c, { owner: e.owner, note: e.note })} className="border border-gray-400 rounded-md px-3 py-1 text-sm hover:bg-gray-50">บันทึก</button>
              <button type="button" onClick={() => save(c, { status: done ? 'open' : 'done' })} className={`rounded-md px-3 py-1 text-sm ${done ? 'border border-gray-400 hover:bg-gray-50' : 'bg-green-600 text-white hover:bg-green-700'}`}>{done ? 'เปิดใหม่' : 'ทำเสร็จแล้ว'}</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- page ---------- */

function ChatFeedbackDashboard() {
  const [isSupervisor, setIsSupervisor] = useState(null);
  const [tab, setTab] = useState('queue');
  const [page, setPage] = useState('');
  const [startDate, setStartDate] = useState(isoDate(Date.now() - 30 * 24 * 3600 * 1000));
  const [endDate, setEndDate] = useState(isoDate(Date.now()));
  const [openCaseId, setOpenCaseId] = useState(null);

  useEffect(() => {
    ChatFeedbackService.getMe().then((res) => setIsSupervisor(!!res.data?.isSupervisor)).catch(() => setIsSupervisor(false));
  }, []);

  if (isSupervisor === null) return <div className="p-8 text-center text-gray-400">กำลังตรวจสอบสิทธิ์...</div>;
  if (!isSupervisor) return <div className="p-8 text-center text-gray-500">หน้านี้เปิดได้เฉพาะหัวหน้า</div>;

  const filters = { page, startDate, endDate };
  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold text-gray-800">Dashboard Feedback แชท</h1>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => setTab(t.key)} className={`px-3 py-1.5 rounded-full text-sm border ${tab === t.key ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300'}`}>{t.label}</button>
          ))}
        </div>
        {tab !== 'backoffice' && (
          <div className="flex items-center gap-2 ml-auto text-sm">
            <select value={page} onChange={(e) => setPage(e.target.value)} className="border rounded px-2 py-1"><option value="">ทั้งสองเพจ</option><option value="Tama">TamaChan</option><option value="Smile">Smile</option></select>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded px-2 py-1" />
            <span className="text-gray-400">ถึง</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border rounded px-2 py-1" />
          </div>
        )}
      </div>

      {tab === 'queue' && <ReviewQueue filters={filters} onOpenCase={setOpenCaseId} />}
      {tab === 'admins' && <AdminStats filters={filters} onOpenCase={setOpenCaseId} />}
      {tab === 'backoffice' && <Backoffice />}

      <CaseModal recipientId={openCaseId} onClose={() => setOpenCaseId(null)} />
    </div>
  );
}

export default ChatFeedbackDashboard;
